import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { TextInput } from "flowbite-react";
import { Surface } from "../components/atoms/Surface";
import { StatusBadge } from "../components/atoms/StatusBadge";
import { Spinner } from "../components/atoms/Spinner";
import { AlertMessage } from "../components/atoms/AlertMessage";
import { ModalShell } from "../components/atoms/ModalShell";
import {
  HiRefresh,
  HiShieldCheck,
  HiExclamation,
  HiPlus,
  HiOutlineTrash,
  HiBan,
} from "react-icons/hi";
import useAuth from "../hooks/useAuth";
import HttpService from "../services/http";
import { toast } from "react-toastify";
import { CustomButton } from "../components/atoms/CustomButton";
import { FieldLabel } from "../components/atoms/FieldLabel";

interface VirusTotalStats {
  undetected: number;
  harmless: number;
  malicious: number;
  suspicious: number;
  timeout: number;
  confirmed_timeout: number;
  failure: number;
  type_unsupported: number;
}

interface VirusTotalEngineResult {
  category: string;
  engine_name: string;
  method: string;
  result: string;
}

interface DomainScanResult {
  url: string;
  status: string;
  is_completed: boolean;
  is_flagged: boolean;
  stats: Partial<VirusTotalStats>;
  scan_date: number | null;
  analysis_id: string | null;
  results: Record<string, VirusTotalEngineResult>;
  error?: string;
}

interface DomainsScanData {
  any_flagged: boolean;
  scanned_at: string;
  domains: DomainScanResult[];
}

interface UrlCheckerResult {
  url: string;
  result: {
    score: number;
    contents: string[];
    rejectionReasons: string[];
  };
}

const DEFAULT_URLS = [
  "https://dashboard.super-user-manager.com/",
  "https://super-user-manager.com/",
];
const MAX_URLS = 2;
const MAX_CHECK_URLS = 20;

const isValidHttpUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const Security: React.FC = () => {
  const { customer_identification, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DomainsScanData | null>(null);
  const [urls, setUrls] = useState<string[]>(DEFAULT_URLS);
  const [draftUrl, setDraftUrl] = useState<string>("");

  const [checkUrls, setCheckUrls] = useState<string[]>([]);
  const [checkDraft, setCheckDraft] = useState<string>("");
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkResults, setCheckResults] = useState<UrlCheckerResult[] | null>(
    null
  );

  const [showBannedModal, setShowBannedModal] = useState(false);
  const [bannedDomains, setBannedDomains] = useState<string[] | null>(null);
  const [bannedLoading, setBannedLoading] = useState(false);
  const [bannedDraft, setBannedDraft] = useState<string>("");

  const urlCheckerBase = `/${customer_identification}/${user.entity_identification}/system/url-checker`;

  const loadBannedDomains = async () => {
    try {
      setBannedLoading(true);
      const response = await HttpService.get(
        `${urlCheckerBase}/banned-domains`
      );
      setBannedDomains(response?.data?.element || []);
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Failed to load banned domains"
      );
    } finally {
      setBannedLoading(false);
    }
  };

  const handleAddBannedDomain = async () => {
    const trimmed = bannedDraft.trim();
    if (!trimmed) return;
    try {
      setBannedLoading(true);
      await HttpService.post(`${urlCheckerBase}/banned-domains`, {
        domains: [trimmed],
      });
      setBannedDraft("");
      toast.success(`Domain "${trimmed}" banned.`);
      await loadBannedDomains();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Failed to add banned domain"
      );
      setBannedLoading(false);
    }
  };

  const handleRemoveBannedDomain = async (domain: string) => {
    try {
      setBannedLoading(true);
      await HttpService.delete(`${urlCheckerBase}/banned-domains`, {
        data: { domains: [domain] },
      });
      toast.success(`Domain "${domain}" removed.`);
      await loadBannedDomains();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Failed to remove banned domain"
      );
      setBannedLoading(false);
    }
  };

  const handleCloseBannedModal = () => {
    setShowBannedModal(false);
    setBannedDraft("");
  };

  useEffect(() => {
    if (showBannedModal && bannedDomains === null) {
      loadBannedDomains();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBannedModal]);

  const handleAddUrl = () => {
    const trimmed = draftUrl.trim();
    if (!trimmed) return;
    if (!isValidHttpUrl(trimmed)) {
      toast.error("Please enter a valid http(s) URL.");
      return;
    }
    if (urls.includes(trimmed)) {
      toast.info("This URL is already in the list.");
      return;
    }
    if (urls.length >= MAX_URLS) {
      toast.error(`Maximum ${MAX_URLS} URLs per scan.`);
      return;
    }
    setUrls([...urls, trimmed]);
    setDraftUrl("");
  };

  const handleRemoveUrl = (url: string) => {
    setUrls(urls.filter((u) => u !== url));
  };

  const handleResetDefaults = () => {
    setUrls(DEFAULT_URLS);
  };

  const fetchScan = async () => {
    if (urls.length === 0) {
      toast.error("Add at least one URL to scan.");
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const response = await HttpService.post(
        `/${customer_identification}/${user.entity_identification}/system/security/domains-scan`,
        { urls }
      );

      setData(response?.data?.element);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to scan URLs";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCheckUrl = () => {
    const trimmed = checkDraft.trim();
    if (!trimmed) return;
    if (!isValidHttpUrl(trimmed)) {
      toast.error("Please enter a valid http(s) URL.");
      return;
    }
    if (checkUrls.includes(trimmed)) {
      toast.info("This URL is already in the list.");
      return;
    }
    if (checkUrls.length >= MAX_CHECK_URLS) {
      toast.error(`Maximum ${MAX_CHECK_URLS} URLs per check.`);
      return;
    }
    setCheckUrls([...checkUrls, trimmed]);
    setCheckDraft("");
  };

  const runUrlCheck = async () => {
    if (checkUrls.length === 0) {
      toast.error("Add at least one URL to check.");
      return;
    }
    try {
      setCheckLoading(true);
      const response = await HttpService.post(`${urlCheckerBase}/check-url`, {
        urls: checkUrls,
      });
      setCheckResults(response?.data?.element || []);
    } catch (err: any) {
      toast.error(
        err.response?.data?.message || err.message || "URL check failed"
      );
    } finally {
      setCheckLoading(false);
    }
  };

  const renderUrlCheckResult = (item: UrlCheckerResult) => {
    const score = item.result?.score ?? 0;
    const reasons = item.result?.rejectionReasons || [];
    const safe = score >= 0.7;
    const risky = score < 0.4;
    return (
      <div
        key={item.url}
        className={`p-3 rounded-md border ${
          safe
            ? "bg-green-50 border-green-200"
            : risky
            ? "bg-red-50 border-red-200"
            : "bg-amber-50 border-amber-200"
        }`}
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <span className="text-sm font-medium text-gray-900 break-all">
            {item.url}
          </span>
          <StatusBadge color={safe ? "success" : risky ? "failure" : "warning"}>
            Score: {score}
          </StatusBadge>
        </div>
        {reasons.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {reasons.map((r) => (
              <span
                key={r}
                className="text-xs px-2 py-0.5 rounded bg-white border border-gray-200 text-gray-700"
              >
                {r}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderDomainCard = (domain: DomainScanResult) => {
    const detections =
      (domain.stats.malicious || 0) + (domain.stats.suspicious || 0);
    const totalEngines =
      (domain.stats.harmless || 0) +
      (domain.stats.malicious || 0) +
      (domain.stats.suspicious || 0) +
      (domain.stats.undetected || 0);

    let badge: React.ReactNode;
    if (domain.error || domain.status === "error") {
      badge = (
        <StatusBadge color="failure" icon={HiExclamation}>
          Error
        </StatusBadge>
      );
    } else if (!domain.is_completed) {
      badge = (
        <StatusBadge color="warning">Pending ({domain.status})</StatusBadge>
      );
    } else if (domain.is_flagged) {
      badge = (
        <StatusBadge color="failure" icon={HiExclamation}>
          Flagged
        </StatusBadge>
      );
    } else {
      badge = (
        <StatusBadge color="success" icon={HiShieldCheck}>
          Clean
        </StatusBadge>
      );
    }

    return (
      <Surface key={domain.url}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 break-all">
              {domain.url}
            </h3>
            {domain.analysis_id && (
              <p className="text-xs text-gray-500 mt-1">
                Analysis ID:{" "}
                <span className="font-mono">{domain.analysis_id}</span>
              </p>
            )}
            {domain.scan_date && (
              <p className="text-xs text-gray-500">
                Scanned at: {new Date(domain.scan_date * 1000).toLocaleString()}
              </p>
            )}
            {domain.error && (
              <p className="text-xs text-red-600 mt-1">{domain.error}</p>
            )}
          </div>
          {badge}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <div className="p-3 bg-red-50 rounded-lg border border-red-200">
            <p className="text-xs text-red-700 mb-1">Malicious</p>
            <p className="text-2xl font-bold text-red-700">
              {domain.stats.malicious || 0}
            </p>
          </div>
          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-xs text-amber-700 mb-1">Suspicious</p>
            <p className="text-2xl font-bold text-amber-700">
              {domain.stats.suspicious || 0}
            </p>
          </div>
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <p className="text-xs text-green-700 mb-1">Harmless</p>
            <p className="text-2xl font-bold text-green-700">
              {domain.stats.harmless || 0}
            </p>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-700 mb-1">Undetected</p>
            <p className="text-2xl font-bold text-gray-700">
              {domain.stats.undetected || 0}
            </p>
          </div>
        </div>

        <div className="mt-3 text-sm text-gray-600">
          <span className="font-semibold">{detections}</span> /{" "}
          <span className="font-semibold">{totalEngines}</span> engines flagged
          this URL {" — "}
          status: <span className="font-semibold">{domain.status}</span>
        </div>

        {domain.is_flagged && (
          <div className="mt-3">
            <h4 className="text-sm font-semibold text-red-700 mb-2">
              Flagging engines
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
              {Object.values(domain.results)
                .filter(
                  (engine) =>
                    engine.category === "malicious" ||
                    engine.category === "suspicious"
                )
                .map((engine) => (
                  <div
                    key={engine.engine_name}
                    className="p-2 bg-red-50 rounded border border-red-200 text-xs"
                  >
                    <div className="font-semibold text-red-800">
                      {engine.engine_name}
                    </div>
                    <div className="text-red-700">
                      {engine.category} — {engine.result}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </Surface>
    );
  };

  return (
    <div className="security-page">
      <Helmet>
        <title>Security | Super User Manager</title>
        <meta
          name="description"
          content="Security tools: check the reputation of SUM domains and user-provided URLs"
        />
      </Helmet>

      <div className="container mx-auto py-6">
        <div className="mb-6">
          <h1 className="text-gray-900 text-2xl font-bold font-figtree leading-loose flex items-center gap-3">
            <HiShieldCheck className="text-blue-600" size={32} />
            Security
          </h1>
          <p className="text-gray-500 text-sm">
            Security tools for the admin panel
          </p>
        </div>

        <div className="mb-4">
          <h2 className="text-gray-900 text-xl font-bold font-figtree leading-loose">
            E-Reputation
          </h2>
          <p className="text-gray-500 text-sm">
            Check the reputation of SUM domains or any user-provided URL against
            security threat databases
          </p>
        </div>

        <Surface className="mb-6">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h2 className="text-lg font-semibold text-gray-900">
              URLs to scan ({urls.length}/{MAX_URLS})
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <CustomButton
                variant="secondary"
                size="xs"
                onClick={handleResetDefaults}
                disabled={loading}
                title="Reset to SUM defaults"
              />
              <CustomButton
                variant="primary"
                size="xs"
                onClick={fetchScan}
                disabled={loading || urls.length === 0}
                icon={
                  <HiRefresh
                    className={loading ? "animate-spin" : ""}
                    size={14}
                  />
                }
                title={
                  loading
                    ? "Scanning..."
                    : data
                    ? `Re-scan (${urls.length})`
                    : `Run scan (${urls.length})`
                }
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <TextInput
              type="url"
              placeholder="https://example.com"
              value={draftUrl}
              onChange={(e) => setDraftUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddUrl();
                }
              }}
              disabled={loading || urls.length >= MAX_URLS}
              className="flex-1"
            />
            <CustomButton
              variant="primary"
              onClick={handleAddUrl}
              disabled={loading || urls.length >= MAX_URLS}
              icon={<HiPlus size={18} />}
              title="Add"
            />
          </div>

          {urls.length === 0 ? (
            <p className="text-sm text-gray-500 italic">
              No URLs added. Add at least one to run a scan.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
              {urls.map((url) => (
                <li
                  key={url}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                >
                  <span
                    className="text-sm text-gray-800 truncate min-w-0"
                    title={url}
                  >
                    {url}
                  </span>
                  <CustomButton
                    variant="none"
                    size="icon"
                    onClick={() => handleRemoveUrl(url)}
                    disabled={loading}
                    className="shrink-0 rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                    aria-label={`Remove ${url}`}
                    icon={<HiOutlineTrash size={18} />}
                  />
                </li>
              ))}
            </ul>
          )}
        </Surface>

        {error && (
          <AlertMessage color="failure" className="mb-4">
            <span className="font-medium">Error!</span> {error}
          </AlertMessage>
        )}

        {loading && !data && (
          <div className="flex flex-col justify-center items-center h-64 gap-3">
            <Spinner size="xl" />
            <p className="text-gray-500 text-sm">
              Submitting URLs to VirusTotal and waiting for analysis. This can
              take up to a couple of minutes — VirusTotal scans each URL with
              70+ engines and rate-limits the API.
            </p>
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-1 gap-6">
              {data.domains.map(renderDomainCard)}
            </div>
          </>
        )}

        <div className="mt-10 mb-4">
          <h2 className="text-gray-900 text-xl font-bold font-figtree leading-loose">
            URL Reputation Check
          </h2>
          <p className="text-gray-500 text-sm">
            Score URLs against the URL Checker service (DNS, HTTPS, banned-list,
            phishing database, Google Web Risk)
          </p>
        </div>

        <Surface className="mb-6">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="text-lg font-semibold text-gray-900">
              URLs to check ({checkUrls.length}/{MAX_CHECK_URLS})
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              <CustomButton
                variant="secondary"
                size="xs"
                onClick={() => setShowBannedModal(true)}
                icon={<HiBan size={14} />}
                title="Manage banned domains"
              />
              <CustomButton
                variant="primary"
                size="xs"
                onClick={runUrlCheck}
                disabled={checkLoading || checkUrls.length === 0}
                icon={
                  <HiRefresh
                    className={checkLoading ? "animate-spin" : ""}
                    size={14}
                  />
                }
                title={
                  checkLoading
                    ? "Checking..."
                    : `Run check (${checkUrls.length})`
                }
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <TextInput
              type="url"
              placeholder="https://example.com"
              value={checkDraft}
              onChange={(e) => setCheckDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddCheckUrl();
                }
              }}
              disabled={checkLoading || checkUrls.length >= MAX_CHECK_URLS}
              className="flex-1"
            />
            <CustomButton
              variant="primary"
              onClick={handleAddCheckUrl}
              disabled={checkLoading || checkUrls.length >= MAX_CHECK_URLS}
              icon={<HiPlus size={18} />}
              title="Add"
            />
          </div>

          {checkUrls.length === 0 ? (
            <p className="text-sm text-gray-500 italic">
              No URLs added. Add at least one to run a check.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md mb-3">
              {checkUrls.map((url) => (
                <li
                  key={url}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                >
                  <span
                    className="text-sm text-gray-800 truncate min-w-0"
                    title={url}
                  >
                    {url}
                  </span>
                  <CustomButton
                    variant="none"
                    size="icon"
                    onClick={() =>
                      setCheckUrls(checkUrls.filter((u) => u !== url))
                    }
                    disabled={checkLoading}
                    className="shrink-0 rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                    aria-label={`Remove ${url}`}
                    icon={<HiOutlineTrash size={18} />}
                  />
                </li>
              ))}
            </ul>
          )}

          {checkResults && checkResults.length > 0 && (
            <div className="flex flex-col gap-2 mt-3">
              {checkResults.map(renderUrlCheckResult)}
            </div>
          )}
        </Surface>
      </div>

      <ModalShell
        show={showBannedModal}
        onClose={handleCloseBannedModal}
        size="2xl"
        title={
          <span className="flex min-w-0 items-center gap-3">
            <span className="rounded-lg bg-red-100 p-2">
              <HiBan className="text-red-600" size={20} />
            </span>
            <span className="truncate">Banned Domains</span>
          </span>
        }
        subtitle="Manage the URL Checker banned-domains list"
        footer={
          <CustomButton
            onClick={handleCloseBannedModal}
            disabled={bannedLoading}
            variant="secondary"
            title="Close"
          />
        }
      >
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-red-50 to-amber-50 rounded-lg p-5 border border-red-200">
            <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <HiPlus className="text-red-600" />
              Ban a domain
            </h4>
            <div className="space-y-4">
              <div>
                <FieldLabel
                  htmlFor="banned-domain-input"
                  value="Domain pattern"
                  className="mb-2 block text-sm font-medium text-gray-700"
                />
                <TextInput
                  id="banned-domain-input"
                  type="text"
                  placeholder="example.com or *.example.com"
                  value={bannedDraft}
                  onChange={(e) => setBannedDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddBannedDomain();
                    }
                  }}
                  disabled={bannedLoading}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Wildcards are supported (e.g. <code>*.malicious.com</code>).
                </p>
              </div>
              <CustomButton
                onClick={handleAddBannedDomain}
                disabled={bannedLoading || !bannedDraft.trim()}
                variant="danger"
                title="Ban domain"
                icon="banCurrent"
              />
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <HiBan className="text-gray-700" />
                {bannedDomains === null
                  ? "Current banned domains"
                  : `Current banned domains (${bannedDomains.length})`}
              </h4>
              <CustomButton
                variant="secondary"
                size="xs"
                onClick={loadBannedDomains}
                disabled={bannedLoading}
                title={
                  <>
                    <HiRefresh
                      className={`mr-1 ${bannedLoading ? "animate-spin" : ""}`}
                      size={14}
                    />
                    Refresh
                  </>
                }
              />
            </div>

            {bannedLoading && bannedDomains === null ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="md" />
                <span className="ml-2 text-sm text-gray-600">
                  Loading banned domains...
                </span>
              </div>
            ) : bannedDomains === null ? (
              <AlertMessage color="info">
                <span className="text-sm">Banned domains not loaded.</span>
              </AlertMessage>
            ) : bannedDomains.length === 0 ? (
              <AlertMessage color="info">
                <span className="text-sm">
                  No banned domains. Add one above to populate the list.
                </span>
              </AlertMessage>
            ) : (
              <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg bg-white max-h-72 overflow-y-auto">
                {bannedDomains.map((domain) => (
                  <li
                    key={domain}
                    className="flex items-center justify-between gap-3 px-4 py-2.5"
                  >
                    <span
                      className="text-sm text-gray-800 truncate min-w-0 font-mono"
                      title={domain}
                    >
                      {domain}
                    </span>
                    <CustomButton
                      variant="none"
                      size="icon"
                      onClick={() => handleRemoveBannedDomain(domain)}
                      disabled={bannedLoading}
                      className="shrink-0 rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                      aria-label={`Remove ${domain}`}
                      icon={<HiOutlineTrash size={18} />}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </ModalShell>
    </div>
  );
};

export default Security;
